import { type ReactNode } from 'react';

import { IconDisc } from '../../primitives/IconDisc/IconDisc';
import styles from './MetricCard.module.css';

/**
 * MetricCard — one headline figure with a label, an icon and an optional
 * hint. `MetricGrid` lays a row of them out.
 *
 * ## Why this is a composite and not two local compositions
 *
 * The dashboard and the reports screen each grew their own metric tile, from
 * the same handoff paragraph, three milestones apart: different grounds
 * (`--color-surface-veiled` against `--color-surface`), different heading
 * levels, different value type scales, and only one of the two ever measured
 * for contrast. That is the exact drift `Field` was extracted to stop for
 * text inputs, and the fix is the same.
 *
 * ## `<section>` + `<h3>` + `<p>`, not a `<div>` with big text
 *
 * A figure with no accessible name is a number a screen-reader user hears
 * with no idea what it counts. The heading names it, `aria-labelledby` binds
 * it, and the icon is `aria-hidden` because it repeats that same label —
 * left visible to assistive technology it renames the tile to "folders Cases
 * in range".
 *
 * `<h3>` is fixed rather than configurable: every screen that uses this puts
 * its own `<h2>` above (the shell owns the one `<h1>`), so a tile heading is
 * always one level below the section that introduces it. A `level` prop would
 * be a way to get that wrong.
 *
 * ## The value is a STRING, and callers pass `'—'` for "not known"
 *
 * Deliberately not `number | null`. A tile that renders `0` for an unknown
 * value states something false, and one that renders nothing is
 * indistinguishable from a broken layout — but WHICH placeholder, and whether
 * a zero is even possible, is a question about the metric rather than about
 * the card. Formatting (an em dash, "2.5 days", a grouped thousand) belongs
 * at the call site, which is the only place that knows.
 */

export interface MetricCardProps {
  /** Names what the figure counts. Becomes the tile's accessible name. */
  label: string;
  /** Already formatted. `'—'` for a value that is not known. */
  value: string;
  /** Decorative; `aria-hidden`, because it repeats `label`. */
  icon?: ReactNode;
  /** One line under the value — what the figure includes, or excludes. */
  hint?: string;
  /** Required: a bare figure is untestable and unnameable. */
  testId: string;
}

export function MetricCard({ label, value, icon, hint, testId }: MetricCardProps) {
  return (
    <section className={styles['card']} aria-labelledby={`${testId}-label`} data-testid={testId}>
      {/* The welcome card's language: the icon in an accent disc, beside the
          label rather than stacked above it, so the tile is one short head
          row, the figure, and the hint. */}
      <div className={styles['head']}>
        {icon === undefined ? null : <IconDisc size="sm">{icon}</IconDisc>}
        <h3 className={styles['label']} id={`${testId}-label`}>
          {label}
        </h3>
      </div>

      <p className={styles['value']}>
        {/* Handoff, Arabic & RTL: a figure never mirrors. */}
        <bdi dir="ltr">{value}</bdi>
      </p>

      {hint === undefined ? null : <p className={styles['hint']}>{hint}</p>}
    </section>
  );
}

/** The row a set of cards sits in. Wraps to a column on a narrow screen. */
export function MetricGrid({ children }: { children: ReactNode }) {
  return <div className={styles['grid']}>{children}</div>;
}
